import { DatePipe } from '@angular/common';
import { Component, effect, inject, OnInit, signal, ChangeDetectionStrategy } from '@angular/core';
import { form, FormField, email, maxLength } from '@angular/forms/signals';
import { IConfigEmpresaRes } from '@core/interfaces/request-response/empresa.dto.interface';
import { EmpresaApiService } from '@core/services/api/empresa-api.service';
import { AppService } from '@shared/services/app.service';
import { SucursalService } from '@shared/services/sucursal.service';
import { UiProgressSpinner, UiInputMask } from '@shared/components/ui';
import { FormsModule } from '@angular/forms';
import { CargarFoto } from '../cargar-foto/cargar-foto';
import { FormErrorMessagePipe } from '@shared/pipes/form-error-message.pipe';
import { buildDiff, normalizarTelefono } from '@shared/utils/functions.utils';
import { ToastService } from '@shared/services/toast.service';

@Component({
  selector: 'app-config-empresa',
  imports: [
    UiProgressSpinner,
    FormField,
    DatePipe,
    UiInputMask,
    FormsModule,
    CargarFoto,
    FormErrorMessagePipe,
  ],
  templateUrl: './config-empresa.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './config-empresa.css',
})
export class ConfigEmpresa implements OnInit {
  constructor() {
    effect(() => {
      if (this.sucursalService.sucursalReady() && this.appService.authReady()) {
        this.cargarConfigEmpresa();
      }
    });
  }
  // servicios
  private empresaApiService = inject(EmpresaApiService);
  private appService = inject(AppService);
  private sucursalService = inject(SucursalService);
  private toastService = inject(ToastService);

  // datos
  empresa = signal<IConfigEmpresaRes | null>(null);
  formulario_empresa = form(
    signal({
      direccion: '',
      email: '',
    }),
    (x) => {
      email(x.email);
      maxLength(x.email, 120, { message: 'El email no puede exceder los 120 caracteres.' });
      maxLength(x.direccion, 200, { message: 'La dirección no puede exceder los 200 caracteres.' });
    },
  );

  numero_celular?: string;
  // metodos
  private cargarConfigEmpresa() {
    this.empresaApiService.cargarEmpresa().subscribe({
      next: (empresa) => {
        this.empresa.set(empresa);

        this.formulario_empresa().value.set({
          direccion: empresa.direccion || '',
          email: empresa.email,
        });
        this.numero_celular = empresa.telefono || undefined;
      },
      error: (err) => {
        console.error('Error al cargar la empresa', err);
      },
    });
  }
  private checkInvalidForm() {
    if (this.formulario_empresa().invalid()) {
      this.formulario_empresa.email().markAsTouched();
      this.formulario_empresa.direccion().markAsTouched();
      return true;
    }
    return false;
  }
  guardarEmpresa() {
    if (this.checkInvalidForm() || !this.empresa()) return;
    const { direccion, email } = this.formulario_empresa().controlValue();
    const telefono = normalizarTelefono(this.numero_celular || '');

    const data = buildDiff(
      {
        direccion: this.empresa()!.direccion || '',
        email: this.empresa()!.email || '',
        telefono: normalizarTelefono(this.empresa()!.telefono || ''),
      },
      {
        direccion: direccion,
        email: email,
        telefono: normalizarTelefono(telefono),
      },
    );
    if (Object.keys(data).length === 0) {
      this.toastService.show(
        'top-right',
        'info',
        'Sin cambios',
        'No se han realizado cambios para guardar.',
      );
      return;
    }
    this.empresaApiService.guardar(data).subscribe({
      next: () => {
        this.cargarConfigEmpresa();
        this.toastService.show(
          'top-right',
          'success',
          'Éxito',
          'La empresa ha sido actualizada correctamente.',
        );
      },
      error: (err) => {
        if (err.status == 401) return;
        this.toastService.show(
          'top-right',
          'error',
          'Error',
          err.error?.message ||
            'Hubo un problema al actualizar la empresa. Por favor, inténtalo de nuevo.',
        );
        console.error('Error al guardar la empresa', err);
      },
    });
  }

  ngOnInit(): void {
    this.appService.setPanelHeader({
      title: 'Configuración de la empresa',
      subtitle: 'Aquí puedes ver la información de la empresa',
      showBackButton: true,
    });
  }
}
